import type { AudioSettings, Settings } from '../core/Settings';

/**
 * Prosedürel ses motoru.
 * Hiçbir ses dosyası yüklenmez: tüm efektler Web Audio API osilatör ve
 * gürültü (noise) düğümleriyle anlık sentezlenir. Tarayıcı autoplay
 * politikası nedeniyle AudioContext ancak ilk kullanıcı etkileşiminde
 * (tık/dokunma/tuş) oluşturulabilir — bunun için `resume()` dışarıdan
 * (main.ts/UI.ts) bir "pointerdown"/"keydown" dinleyicisinden çağrılır.
 *
 * Ses kategorileri Settings.audio'daki dört kanaldan geçer:
 *  - master: hepsinin üstünde genel seviye
 *  - sfx: vuruş/toplama/inşaat/UI gibi anlık efektler
 *  - music: şu an kullanılmıyor (ileride prosedürel müzik için hazır)
 *  - ambient: biyom/gece-gündüz döngü sesi (sürekli filtreli gürültü)
 */

type Surface = 'grass' | 'stone' | 'sand' | 'wood' | 'water' | 'ice';
type ToneOpts = {
  type?: OscillatorType;
  gain?: number;
  attack?: number;
  decay?: number;
  /** Frekansın süre boyunca kayacağı miktar (Hz, + veya -). */
  sweep?: number;
};
type NoiseOpts = {
  filterType?: BiquadFilterType;
  freq?: number;
  q?: number;
  gain?: number;
};

const AMBIENT_PARAMS: Record<string, { type: BiquadFilterType; freq: number; q: number; gain: number }> = {
  plains: { type: 'lowpass', freq: 1800, q: 0.4, gain: 0.05 },
  forest: { type: 'bandpass', freq: 900, q: 0.7, gain: 0.06 },
  wind: { type: 'highpass', freq: 700, q: 0.5, gain: 0.09 },
  waves: { type: 'lowpass', freq: 500, q: 0.6, gain: 0.1 },
  swamp: { type: 'lowpass', freq: 350, q: 0.8, gain: 0.08 },
  venom: { type: 'bandpass', freq: 250, q: 1.4, gain: 0.07 },
};

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private ambientGain: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private ambient: { src: AudioBufferSourceNode; filter: BiquadFilterNode; gain: GainNode; id: string; night: boolean } | null = null;
  private critterTimer = 3 + Math.random() * 4;

  constructor(private settings: Settings) {
    settings.events.on('audio:changed', (a) => this.applyGains(a));
  }

  /** İlk kullanıcı etkileşiminde çağrılır; tekrar çağrılırsa güvenli no-op/resume. */
  resume(): void {
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      const ctx = new Ctor();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.sfxGain = ctx.createGain();
      this.musicGain = ctx.createGain();
      this.ambientGain = ctx.createGain();
      this.sfxGain.connect(this.master);
      this.musicGain.connect(this.master);
      this.ambientGain.connect(this.master);
      this.master.connect(ctx.destination);
      this.noiseBuffer = this.buildNoiseBuffer(ctx);
      this.applyGains(this.settings.audio);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  private applyGains(a: AudioSettings): void {
    if (!this.master || !this.sfxGain || !this.musicGain || !this.ambientGain) return;
    this.master.gain.value = a.master;
    this.sfxGain.gain.value = a.sfx;
    this.musicGain.gain.value = a.music;
    this.ambientGain.gain.value = a.ambient;
  }

  private buildNoiseBuffer(ctx: AudioContext): AudioBuffer {
    const len = Math.floor(ctx.sampleRate * 2);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    return buf;
  }

  // ---------------------------------------------------------------- ilkel sesler

  private tone(freq: number, duration: number, opts: ToneOpts = {}): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain) return;
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(Math.max(20, freq), t0);
    if (opts.sweep) osc.frequency.exponentialRampToValueAtTime(Math.max(20, freq + opts.sweep), t0 + duration);
    const g = ctx.createGain();
    const peak = opts.gain ?? 0.25;
    const attack = opts.attack ?? 0.005;
    const decay = Math.max(0.02, opts.decay ?? duration);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
    osc.connect(g);
    g.connect(this.sfxGain);
    osc.start(t0);
    osc.stop(t0 + attack + decay + 0.05);
  }

  /** Ardışık nota dizisi (başarı/görev/gün başı gibi kısa "chime" efektleri). */
  private chime(freqs: number[], gap: number, opts: ToneOpts = {}): void {
    const ctx = this.ctx;
    if (!ctx) return;
    freqs.forEach((f, i) => {
      const delayed = () => this.tone(f, opts.decay ?? 0.16, opts);
      if (i === 0) delayed();
      else setTimeout(delayed, i * gap * 1000);
    });
  }

  private noiseBurst(duration: number, opts: NoiseOpts = {}): void {
    const ctx = this.ctx;
    if (!ctx || !this.sfxGain || !this.noiseBuffer) return;
    const t0 = ctx.currentTime;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = opts.filterType ?? 'bandpass';
    filter.frequency.value = opts.freq ?? 1200;
    filter.Q.value = opts.q ?? 1;
    const g = ctx.createGain();
    const peak = opts.gain ?? 0.3;
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(peak, t0 + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.sfxGain);
    src.start(t0);
    src.stop(t0 + duration + 0.05);
  }

  // ---------------------------------------------------------------- SFX API

  footstep(surface: Surface, hard = false): void {
    const gain = hard ? 0.28 : 0.16;
    switch (surface) {
      case 'stone':
        this.noiseBurst(0.05, { filterType: 'highpass', freq: 1900, q: 0.9, gain });
        break;
      case 'sand':
        this.noiseBurst(0.11, { filterType: 'lowpass', freq: 2200, q: 0.3, gain: gain * 0.9 });
        break;
      case 'wood':
        this.noiseBurst(0.05, { filterType: 'bandpass', freq: 1500, q: 1.3, gain });
        break;
      case 'water':
        this.noiseBurst(0.14, { filterType: 'lowpass', freq: 1000, q: 0.5, gain: gain * 1.2 });
        this.tone(220, 0.16, { type: 'sine', sweep: -90, gain: gain * 0.5 });
        break;
      case 'ice':
        this.noiseBurst(0.05, { filterType: 'highpass', freq: 3200, q: 1.4, gain: gain * 0.8 });
        break;
      default:
        this.noiseBurst(0.08, { filterType: 'bandpass', freq: 850, q: 0.6, gain });
    }
  }

  jump(): void {
    this.tone(320, 0.12, { type: 'triangle', sweep: 160, gain: 0.18 });
  }

  land(surface: Surface): void {
    this.footstep(surface, true);
    this.noiseBurst(0.08, { filterType: 'lowpass', freq: 400, gain: 0.16 });
  }

  splash(): void {
    this.noiseBurst(0.28, { filterType: 'lowpass', freq: 900, q: 0.5, gain: 0.32 });
    this.tone(200, 0.22, { type: 'sine', sweep: -100, gain: 0.16 });
  }

  attackSwing(): void {
    this.noiseBurst(0.07, { filterType: 'highpass', freq: 3200, q: 0.7, gain: 0.1 });
  }

  hitCreature(): void {
    this.tone(95, 0.12, { type: 'square', sweep: -35, gain: 0.24 });
    this.noiseBurst(0.05, { filterType: 'bandpass', freq: 2200, gain: 0.14 });
  }

  creatureDeath(): void {
    this.tone(70, 0.4, { type: 'sawtooth', sweep: -45, gain: 0.28 });
  }

  playerHurt(): void {
    this.tone(150, 0.24, { type: 'square', sweep: -60, gain: 0.3 });
  }

  playerDeath(): void {
    this.tone(240, 0.9, { type: 'sawtooth', sweep: -190, gain: 0.3, decay: 0.9 });
  }

  chop(): void {
    this.noiseBurst(0.09, { filterType: 'bandpass', freq: 600, q: 2, gain: 0.3 });
    this.tone(140, 0.1, { type: 'triangle', gain: 0.14 });
  }

  mine(): void {
    this.noiseBurst(0.08, { filterType: 'highpass', freq: 2500, q: 1, gain: 0.26 });
    this.tone(320, 0.06, { type: 'square', gain: 0.1 });
  }

  gather(): void {
    this.noiseBurst(0.07, { filterType: 'bandpass', freq: 1100, q: 1.2, gain: 0.18 });
  }

  /** Kaynak türüne göre doğru "vuruş anı" sesini seçer (kırılma anı değil). */
  gatherSoundFor(kind: string | undefined): void {
    if (kind === 'tree' || kind === 'bush' || kind === 'plant') this.chop();
    else if (kind === 'rock' || kind === 'ore') this.mine();
    else this.gather();
  }

  resourceBreak(): void {
    this.noiseBurst(0.16, { filterType: 'lowpass', freq: 750, gain: 0.24 });
    this.tone(180, 0.1, { type: 'triangle', sweep: 60, gain: 0.12 });
  }

  buildPlace(): void {
    this.tone(260, 0.12, { type: 'triangle', gain: 0.2 });
    this.noiseBurst(0.08, { filterType: 'lowpass', freq: 500, gain: 0.14 });
  }

  hammerUpgrade(): void {
    this.tone(480, 0.16, { type: 'square', sweep: 140, gain: 0.15 });
  }

  toolBroken(): void {
    this.tone(180, 0.3, { type: 'sawtooth', sweep: -140, gain: 0.24 });
  }

  chestOpen(): void {
    this.tone(220, 0.18, { type: 'triangle', sweep: 40, gain: 0.15 });
    this.noiseBurst(0.1, { filterType: 'lowpass', freq: 700, gain: 0.1 });
  }

  uiClick(): void {
    this.tone(700, 0.04, { type: 'sine', gain: 0.12 });
  }

  uiToggle(): void {
    this.tone(500, 0.05, { type: 'sine', sweep: 100, gain: 0.1 });
  }

  coin(): void {
    this.chime([880, 1180], 0.045, { type: 'sine', gain: 0.14, decay: 0.1 });
  }

  bowDrawStart(): void {
    this.tone(200, 0.05, { type: 'sine', gain: 0.08 });
  }

  bowRelease(power: number): void {
    this.tone(150 + power * 200, 0.2, { type: 'sawtooth', sweep: 250 + power * 200, gain: 0.2 + power * 0.1 });
  }

  achievement(): void {
    this.chime([523, 659, 784], 0.09, { type: 'triangle', gain: 0.18, decay: 0.2 });
  }

  questDone(): void {
    this.chime([440, 554, 659], 0.08, { type: 'sine', gain: 0.16, decay: 0.16 });
  }

  dayStart(): void {
    this.chime([392, 494], 0.14, { type: 'sine', gain: 0.12, decay: 0.3 });
  }

  eat(): void {
    this.noiseBurst(0.1, { filterType: 'bandpass', freq: 500, q: 3, gain: 0.16 });
  }

  // ---------------------------------------------------------------- ortam (ambient)

  /**
   * Biyom/gece-gündüz ortam sesini günceller. Aynı biyom+gece durumu tekrar
   * geldiğinde hiçbir şey yapmaz (gereksiz yeniden kurulum yapılmaz).
   * Biyom değiştiğinde eski gürültü kaynağı 1.2s içinde söner, yenisi kurulur.
   */
  setAmbient(ambientId: string | undefined, isNight: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.ambientGain) return;
    const id = ambientId ?? 'plains';
    if (this.ambient && this.ambient.id === id && this.ambient.night === isNight) return;

    const old = this.ambient;
    this.ambient = null;
    if (old) {
      const t0 = ctx.currentTime;
      old.gain.gain.cancelScheduledValues(t0);
      old.gain.gain.setValueAtTime(old.gain.gain.value, t0);
      old.gain.gain.linearRampToValueAtTime(0, t0 + 1.2);
      old.src.stop(t0 + 1.3);
    }

    if (!this.noiseBuffer) return;
    const params = AMBIENT_PARAMS[id] ?? AMBIENT_PARAMS.plains!;
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = params.type;
    filter.frequency.value = isNight ? params.freq * 0.8 : params.freq;
    filter.Q.value = params.q;
    const gain = ctx.createGain();
    const targetGain = params.gain * (isNight ? 0.75 : 1);
    const t0 = ctx.currentTime;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(targetGain, t0 + 1.5);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.ambientGain);
    src.start(t0);
    this.ambient = { src, filter, gain, id, night: isNight };
  }

  /**
   * Her sabit karede çağrılır: ova/orman biyomlarında geceleyin ara sıra
   * cırcır böceği/baykuş benzeri kısa cıvıltılar tetikler (ambient'in
   * "hissiz" kalmaması için minimal, tekrar eden bir doku).
   */
  updateAmbientDetail(dt: number, ambientId: string | undefined, isNight: boolean): void {
    if (!this.ctx) return;
    const id = ambientId ?? 'plains';
    if (!isNight || (id !== 'plains' && id !== 'forest')) return;
    this.critterTimer -= dt;
    if (this.critterTimer <= 0) {
      this.critterTimer = 2.5 + Math.random() * 5;
      const base = 1800 + Math.random() * 900;
      this.tone(base, 0.05, { type: 'sine', gain: 0.05, sweep: 200 });
    }
  }

  dispose(): void {
    if (this.ambient) {
      try {
        this.ambient.src.stop();
      } catch {
        /* zaten durmuş olabilir */
      }
      this.ambient = null;
    }
    void this.ctx?.close();
    this.ctx = null;
  }
}
